import type { SubstratZeile } from 'src/app/data-model';

/**
 * „In alle Zeilen darunter übernehmen" für die Stufen-Spalten der
 * Substratfolge (Humusgehalt, Lagerungsdichte, Skelettgehalt, Karbonat;
 * im Moorprotokoll Zersetzungsgrad, Konsistenz, Substanzvolumen; im
 * Grubenprotokoll Festigkeit und Fdwz-Häufung).
 *
 * Nutzer-Wunsch (15.09.2026): Im Substratprofil stehen in diesen Spalten
 * meist dieselben Werte in allen Zeilen; sie Zeile für Zeile zu wählen war
 * lästig. Das Feld (`wd-stufen-input`, Lib) meldet nur den Wert — welche
 * Zeilen darunter liegen, weiß allein die Tabelle. Deshalb steht die Regel
 * hier, ohne Angular, für sich prüfbar.
 */

/** Die Spalten, für die es das Übernehmen gibt. Alle tragen eine Zahl. */
export type StufenFeld =
	| 'humusgehalt' | 'lagerungsdichte' | 'skelettgehalt' | 'karbonatgehalt'
	| 'festigkeit' | 'torfZersetzungsgrad' | 'muddeKonsistenz' | 'substanzvolumen'
	/** Liegt verschachtelt: `feindurchwurzelung.haeufung`. */
	| 'feindurchwurzelungHaeufung';

/**
 * Gilt die Spalte in dieser Zeile? Im Moorprotokoll führt nur eine Torf- oder
 * Quellsediment-Zeile einen Zersetzungsgrad, nur eine Mudde- oder
 * Quellsediment-Zeile eine Konsistenz — dieselbe Regel, nach der die Vorlage
 * das Feld zeigt oder weglässt. „Nach unten" überspringt die anderen Zeilen:
 * Ein Wert in einer Zeile, die das Feld gar nicht zeigt, wäre unsichtbar und
 * trotzdem gespeichert.
 */
export type SpalteGilt = (zeile: SubstratZeile) => boolean;

/** Der Wert der Spalte in einer Zeile. */
export function liesStufe(zeile: SubstratZeile, feld: StufenFeld): number | undefined {
	if (feld === 'feindurchwurzelungHaeufung') return zeile.feindurchwurzelung?.haeufung;
	return zeile[feld];
}

/** Schreibt den Wert der Spalte in eine Zeile; das verschachtelte Objekt entsteht bei Bedarf. */
export function schreibStufe(zeile: SubstratZeile, feld: StufenFeld, wert: number): void {
	if (feld === 'feindurchwurzelungHaeufung') {
		if (!zeile.feindurchwurzelung) zeile.feindurchwurzelung = { dichte: '', haeufung: wert };
		else zeile.feindurchwurzelung.haeufung = wert;
		return;
	}
	zeile[feld] = wert;
}

/**
 * Gibt es unter Zeile i noch Zeilen, in denen die Spalte gilt? In der letzten
 * Zeile gäbe es nichts zu füllen — und unter einer Torfzeile ohne weitere
 * Torfzeile auch nicht.
 */
export function hatZeilenDarunter(zeilen: readonly SubstratZeile[] | undefined, i: number, gilt?: SpalteGilt): boolean {
	if (!Array.isArray(zeilen)) return false;
	if (i < 0 || i >= zeilen.length - 1) return false;
	if (!gilt) return true;
	for (let k = i + 1; k < zeilen.length; k++) {
		if (gilt(zeilen[k])) return true;
	}
	return false;
}

/**
 * Schreibt `wert` in das Feld aller Zeilen unter Zeile i, in denen die Spalte
 * gilt — auch in die, die schon einen anderen Wert haben: „alles ausfüllen,
 * wie in der aktuellen Zelle". Die Zeile i selbst und alle darüber bleiben
 * unberührt.
 *
 * Liefert `true`, wenn sich etwas geändert hat (dann ist der Punkt zu
 * speichern), sonst `false` — stehen unten schon überall dieselben Werte,
 * gibt es nichts zu speichern.
 */
export function fuelleNachUnten(zeilen: SubstratZeile[] | undefined, i: number, feld: StufenFeld, wert: number, gilt?: SpalteGilt): boolean {
	if (!Array.isArray(zeilen) || !hatZeilenDarunter(zeilen, i, gilt)) return false;
	let geaendert = false;
	for (let k = i + 1; k < zeilen.length; k++) {
		const zeile = zeilen[k];
		if (gilt && !gilt(zeile)) continue;
		if (liesStufe(zeile, feld) === wert) continue;
		schreibStufe(zeile, feld, wert);
		geaendert = true;
	}
	return geaendert;
}
